import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import api, { getToken, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PortalSwitcher from "@/components/PortalSwitcher";
import AdminAffiliates from "@/components/AdminAffiliates";
import AdminReferrals from "@/components/AdminReferrals";
import AdminPilotApplications from "@/components/AdminPilotApplications";
import AdminNurseApplications from "@/components/AdminNurseApplications";
import AdminNotificationsFeed from "@/components/AdminNotificationsFeed";
import AdminAttorneyApproveModal from "@/components/AdminAttorneyApproveModal";
import AdminMembershipPlans from "@/components/AdminMembershipPlans";
import AdminPromoCodes from "@/components/AdminPromoCodes";
import AdminBillingModal from "@/components/AdminBillingModal";
import AdminBillingAudit from "@/components/AdminBillingAudit";
import AdminVerification from "@/components/AdminVerification";
import AdminRoutingConfig from "@/components/AdminRoutingConfig";
import AdminTelehealth from "@/components/AdminTelehealth";
import AdminCredentials from "@/components/AdminCredentials";
import NetworkCommand from "@/components/admin/NetworkCommand";
import TestCenter from "@/components/admin/TestCenter";
import DemoModeSettings from "@/components/admin/DemoModeSettings";
import {
  ShieldCheck, LogOut, Users, Scale, ArrowLeftRight, ReceiptText, BarChart3,
  Bell, Check, UserPlus, Play, Loader2, DollarSign, Settings2, Send, Download, FileDown, Sparkles, RotateCcw, Megaphone, Network, CheckCircle2, XCircle, CreditCard, HeartPulse, Ticket, ScrollText, PlayCircle, Route,
  Scale as ScaleIcon, Stethoscope, Building2, Plane, Search as SearchIcon, Map as MapIcon, FlaskConical,
} from "lucide-react";

const NC_TABS = [
  { key: "nc-attorney", label: "Attorney Command", icon: ScaleIcon },
  { key: "nc-rn", label: "RN Command", icon: Stethoscope },
  { key: "nc-telehealth", label: "Telehealth Command", icon: Building2 },
  { key: "nc-drone", label: "Drone Command", icon: Plane },
  { key: "nc-search", label: "Global Search", icon: SearchIcon },
  { key: "nc-map", label: "Network Map", icon: MapIcon },
  { key: "nc-test", label: "Test Center", icon: FlaskConical },
];

const TABS = [
  { key: "overview", label: "Overview", icon: BarChart3 },
  { key: "attorneys", label: "Attorneys", icon: Scale },
  { key: "plans", label: "Membership Plans", icon: CreditCard },
  { key: "promos", label: "Promo Codes", icon: Ticket },
  { key: "billing-audit", label: "Billing Audit", icon: ScrollText },
  { key: "verification", label: "Verification", icon: ShieldCheck },
  { key: "credentials", label: "Credentials", icon: ShieldCheck },
  { key: "routing", label: "Attorney Routing", icon: Route },
  { key: "telehealth", label: "Telehealth", icon: HeartPulse },
  { key: "affiliates", label: "Affiliates", icon: Megaphone },
  { key: "referrals", label: "Referrals", icon: Network },
  { key: "users", label: "Users", icon: Users },
  { key: "transfers", label: "Transfers", icon: ArrowLeftRight },
  { key: "invoices", label: "Invoices", icon: ReceiptText },
  { key: "scoring", label: "Scoring", icon: Sparkles },
  { key: "network", label: "Response Network", icon: Network },
  { key: "nurses", label: "RN Network", icon: HeartPulse },
  { key: "notifications", label: "Notifications", icon: Bell },
  { key: "notify", label: "Notify", icon: Bell },
  { key: "demo-mode", label: "Demo Mode", icon: Sparkles },
  { key: "demo-control-link", label: "Demo Control Center", icon: Sparkles, href: "/demo-control" },
  { key: "compliance-link", label: "Compliance Center", icon: ShieldCheck, href: "/compliance" },
];

const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 0 })}`;

export default function AdminPortal() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("overview");

  const doLogout = async () => { await logout(); navigate("/"); };

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-white/80 border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <Link to="/admin" className="flex items-center gap-2">
            <div className="h-8 w-8 rounded-xl bg-navy flex items-center justify-center"><ShieldCheck className="w-5 h-5 text-white" strokeWidth={2.2} /></div>
            <span className="font-heading font-extrabold text-lg tracking-tight text-navy">Crash Ready <span className="text-cobalt">Admin</span></span>
          </Link>
          <div className="flex items-center gap-3">
            <PortalSwitcher />
            <button onClick={doLogout} data-testid="admin-logout" className="flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium text-slate-600 hover:bg-red-50 hover:text-red-600 transition-all"><LogOut className="w-4 h-4" /> Logout</button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        <p className="text-xs font-medium text-slate-500 uppercase tracking-widest">Admin</p>
        <h1 className="font-heading text-3xl md:text-4xl font-extrabold text-navy mt-1">Control Center</h1>

        <div className="mt-6 flex gap-2 overflow-x-auto pb-1">
          {TABS.map(({ key, label, icon: Icon, href }) => (
            <button key={key} onClick={() => href ? navigate(href) : setTab(key)} data-testid={`admin-tab-${key}`}
              className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all ${tab === key ? "bg-navy text-white" : href ? "bg-cobalt/10 border border-cobalt/30 text-cobalt hover:bg-cobalt/20" : "bg-white border border-slate-200 text-slate-600 hover:bg-slate-50"}`}>
              <Icon className="w-4 h-4" /> {label}
            </button>
          ))}
        </div>

        <div className="mt-4">
          <p className="text-[10px] font-bold text-slate-400 uppercase tracking-[0.2em]">Network Command</p>
          <div className="mt-2 flex gap-2 overflow-x-auto pb-1">
            {NC_TABS.map(({ key, label, icon: Icon }) => (
              <button key={key} onClick={() => setTab(key)} data-testid={`admin-tab-${key}`}
                className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold whitespace-nowrap transition-all ${tab === key ? "bg-cobalt text-white" : "bg-white border border-cobalt/30 text-cobalt hover:bg-cobalt/5"}`}>
                <Icon className="w-4 h-4" /> {label}
              </button>
            ))}
          </div>
        </div>

        <div className="mt-8">
          {tab === "overview" && <Overview onNavigate={setTab} />}
          {tab === "attorneys" && <AttorneysTab />}
          {tab === "plans" && <AdminMembershipPlans />}
          {tab === "promos" && <AdminPromoCodes />}
          {tab === "nurses" && <AdminNurseApplications />}
          {tab === "notifications" && <AdminNotificationsFeed />}
          {tab === "affiliates" && <AdminAffiliates />}
          {tab === "referrals" && <AdminReferrals />}
          {tab === "users" && <UsersTab />}
          {tab === "transfers" && <TransfersTab />}
          {tab === "invoices" && <InvoicesTab />}
          {tab === "scoring" && <ScoringTab />}
          {tab === "network" && <ResponseNetworkTab />}
          {tab === "notify" && <NotifyTab />}
          {tab === "billing-audit" && <AdminBillingAudit />}
          {tab === "verification" && <AdminVerification />}
          {tab === "credentials" && <AdminCredentials />}
          {tab === "routing" && <AdminRoutingConfig />}
          {tab === "telehealth" && <AdminTelehealth />}
          {tab === "nc-attorney" && <NetworkCommand view="attorney" onNavigate={setTab} />}
          {tab === "nc-rn" && <NetworkCommand view="rn" onNavigate={setTab} />}
          {tab === "nc-telehealth" && <NetworkCommand view="telehealth" onNavigate={setTab} />}
          {tab === "nc-drone" && <NetworkCommand view="drone" onNavigate={setTab} />}
          {tab === "nc-search" && <NetworkCommand view="search" />}
          {tab === "nc-map" && <NetworkCommand view="map" />}
          {tab === "nc-test" && <TestCenter />}
          {tab === "demo-mode" && <DemoModeSettings />}
        </div>
      </main>
    </div>
  );
}

const Card = ({ children }) => <div className="bg-white rounded-3xl border border-slate-100 p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">{children}</div>;

function Overview({ onNavigate }) {
  const [s, setS] = useState(null);
  const [fee, setFee] = useState("");
  const [bh, setBh] = useState(null);
  const [da, setDa] = useState(null);
  const [vf, setVf] = useState(null);
  useEffect(() => {
    api.get("/admin/overview").then(({ data }) => { setS(data); setFee(data.referral_fee); }).catch(() => {});
    api.get("/admin/billing/health").then(({ data }) => setBh(data)).catch(() => {});
    api.get("/admin/demo-analytics").then(({ data }) => setDa(data)).catch(() => {});
    api.get("/admin/verification-funnel").then(({ data }) => setVf(data)).catch(() => {});
  }, []);
  if (!s) return <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 text-cobalt animate-spin" /></div>;

  const saveFee = async () => {
    try { const { data } = await api.put("/admin/settings", { referral_fee: Number(fee) }); setFee(data.referral_fee); toast.success("Referral fee updated"); }
    catch { toast.error("Could not update fee"); }
  };

  const stats = [
    { label: "Clients", value: s.users, to: "users" }, { label: "Attorneys", value: s.attorneys, to: "nc-attorney" },
    { label: "Total Cases", value: s.total_cases }, { label: "Available", value: s.available_cases },
    { label: "Claimed", value: s.claimed_cases }, { label: "Avg Score", value: s.avg_case_score, to: "scoring" },
    { label: "Avg Hrs→Claim", value: s.avg_hours_to_claim }, { label: "Referrals", value: s.referrals, to: "referrals" },
    { label: "Billed", value: money(s.revenue_billed), to: "invoices" }, { label: "Collected", value: money(s.revenue_collected), to: "invoices" },
  ];
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {stats.map((x) => (
          <button key={x.label} onClick={() => x.to && onNavigate?.(x.to)} disabled={!x.to}
            data-testid={`admin-overview-stat-${x.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
            className={`text-left bg-white rounded-2xl border border-slate-100 p-5 shadow-[0_8px_30px_rgb(0,0,0,0.04)] ${x.to ? "transition-all hover:border-cobalt hover:-translate-y-0.5 cursor-pointer" : "cursor-default"}`}>
            <div className="font-heading text-3xl font-extrabold text-navy">{x.value}</div>
            <div className="text-xs font-medium text-slate-500 uppercase tracking-wider mt-1">{x.label}</div>
          </button>
        ))}
      </div>
      {bh && (
        <Card>
          <h3 className="font-heading font-semibold text-navy flex items-center gap-2" data-testid="billing-health"><CreditCard className="w-5 h-5 text-cobalt" /> Billing Health</h3>
          <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-4">
            {[
              { label: "MRR", value: money(bh.mrr), accent: "text-emerald-600" },
              { label: "Paying Members", value: bh.paying },
              { label: "Comped", value: bh.comped },
              { label: "Lifetime", value: bh.lifetime },
              { label: "Manually Granted", value: bh.granted },
              { label: "Refunds Recorded", value: `${money(bh.refund_total)} (${bh.refund_count})`, accent: "text-amber-600" },
              { label: "Renewals Next 7 Days", value: bh.renewals_next_7_days },
              { label: "Active Members", value: bh.active_members },
            ].map((x) => (
              <div key={x.label} className="rounded-2xl bg-slate-50 p-4">
                <div className={`font-heading text-2xl font-extrabold ${x.accent || "text-navy"}`}>{x.value}</div>
                <div className="text-[11px] font-medium text-slate-500 uppercase tracking-wider mt-1">{x.label}</div>
              </div>
            ))}
          </div>
        </Card>
      )}
      {vf && (
        <Card>
          <h3 className="font-heading font-semibold text-navy flex items-center gap-2" data-testid="verification-funnel"><ShieldCheck className="w-5 h-5 text-cobalt" /> Verification Funnel</h3>
          <p className="text-sm text-slate-500 mt-1">Where live accident reports drop off across the verification gates. {vf.on_hold > 0 && <span className="text-amber-600 font-medium">{vf.on_hold} case(s) on hold.</span>}</p>
          <div className="mt-4 space-y-1.5">
            {(() => {
              const max = Math.max(...vf.funnel.map((x) => x.count), 1);
              return vf.funnel.map((x) => (
                <div key={x.key} className="flex items-center gap-3" data-testid={`funnel-${x.key}`}>
                  <span className="w-44 shrink-0 text-xs text-slate-500">{x.label}</span>
                  <div className="flex-1 h-5 bg-slate-50 rounded-full overflow-hidden">
                    <div className="h-full bg-cobalt/80 rounded-full transition-all" style={{ width: `${(x.count / max) * 100}%` }} />
                  </div>
                  <span className="w-8 text-right text-sm font-semibold text-navy">{x.count}</span>
                </div>
              ));
            })()}
          </div>
        </Card>
      )}
      {da && (
        <Card>
          <h3 className="font-heading font-semibold text-navy flex items-center gap-2" data-testid="demo-engagement"><PlayCircle className="w-5 h-5 text-cobalt" /> Demo Engagement</h3>
          <p className="text-sm text-slate-500 mt-1">How well the interactive demo converts curious visitors into live accident reports.</p>
          <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-4">
            {[
              { label: "Demo Sessions", value: da.unique_sessions },
              { label: "Demo Opens", value: da.demo_opens },
              { label: "Exits to Live", value: da.exits_to_live, accent: "text-emerald-600" },
              { label: "Conversion Rate", value: `${da.conversion_rate}%`, accent: "text-emerald-600" },
            ].map((x) => (
              <div key={x.label} className="rounded-2xl bg-slate-50 p-4">
                <div className={`font-heading text-2xl font-extrabold ${x.accent || "text-navy"}`}>{x.value}</div>
                <div className="text-[11px] font-medium text-slate-500 uppercase tracking-wider mt-1">{x.label}</div>
              </div>
            ))}
          </div>
          <div className="mt-4 grid sm:grid-cols-3 gap-4 text-sm">
            {[["Scenario opens", da.scenario_opens], ["Simulated actions", da.actions], ["Entry source", da.sources]].map(([label, rows]) => (
              <div key={label} className="rounded-2xl border border-slate-100 p-4">
                <div className="text-xs font-semibold text-slate-400 uppercase tracking-wider">{label}</div>
                <div className="mt-2 space-y-1.5">
                  {(rows || []).length === 0 ? <div className="text-slate-400 text-xs">No events yet</div> :
                    rows.map((r) => (
                      <div key={r.key} className="flex items-center justify-between">
                        <span className="text-slate-600 capitalize">{String(r.key).replace(/_/g, " ")}</span>
                        <span className="font-semibold text-navy">{r.count}</span>
                      </div>
                    ))}
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}
      <Card>
        <h3 className="font-heading font-semibold text-navy flex items-center gap-2"><Settings2 className="w-5 h-5 text-cobalt" /> Referral Fee</h3>
        <p className="text-sm text-slate-500 mt-1">Amount each attorney is invoiced when they take a client off the board.</p>
        <div className="mt-4 flex items-center gap-3 max-w-sm">
          <div className="relative flex-1">
            <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input type="number" value={fee} onChange={(e) => setFee(e.target.value)} data-testid="referral-fee-input" className="w-full bg-slate-50 border border-slate-200 rounded-xl pl-9 pr-4 py-2.5 outline-none focus:border-cobalt" />
          </div>
          <button onClick={saveFee} data-testid="save-fee-btn" className="px-5 py-2.5 rounded-full bg-cobalt text-white font-medium hover:bg-cobalt-hover transition-all">Save</button>
        </div>
      </Card>
    </div>
  );
}

function AttorneysTab() {
  const [attorneys, setAttorneys] = useState([]);
  const [autoApprove, setAutoApprove] = useState(false);
  const [loading, setLoading] = useState(true);
  const [modalAttorney, setModalAttorney] = useState(null);
  const [crmFilter, setCrmFilter] = useState("");
  const [billingAttorney, setBillingAttorney] = useState(null);
  const load = () => {
    setLoading(true);
    Promise.all([api.get("/admin/attorneys"), api.get("/admin/settings").catch(() => ({ data: {} }))])
      .then(([a, s]) => { setAttorneys(a.data); setAutoApprove(!!s.data.attorney_auto_approve); })
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);
  const setStatus = async (id, status) => {
    try { await api.post(`/admin/attorneys/${id}/status`, { status }); toast.success(`Attorney ${status}`); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Action failed"); }
  };
  const verifyBar = async (id) => {
    try { const { data } = await api.post(`/admin/attorneys/${id}/verify-bar`); toast.success(data.bar_verified ? "Bar verified" : "Bar verification cleared"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed"); }
  };
  const toggleAuto = async () => {
    try { const { data } = await api.put("/admin/settings", { attorney_auto_approve: !autoApprove }); setAutoApprove(!!data.attorney_auto_approve); toast.success(`Auto-approve ${!autoApprove ? "on" : "off"}`); }
    catch { toast.error("Failed"); }
  };
  if (loading) return <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 text-cobalt animate-spin" /></div>;

  const STATUS = { pending: "bg-amber-50 text-amber-600", active: "bg-green-50 text-green-600", suspended: "bg-slate-100 text-slate-500", rejected: "bg-red-50 text-red-600" };
  const SUB = { active: "bg-emerald-50 text-emerald-600", none: "bg-amber-50 text-amber-600", suspended: "bg-slate-100 text-slate-500", canceled: "bg-red-50 text-red-600", past_due: "bg-red-50 text-red-600" };
  const matchCrm = (a) => !crmFilter || (crmFilter === "keep_sync"
    ? (a.crm_coverage || []).some((c) => c.keep_synced)
    : (a.crm_coverage || []).some((c) => c.provider === crmFilter));
  const pending = attorneys.filter((a) => a.attorney_status === "pending" && matchCrm(a));
  const others = attorneys.filter((a) => a.attorney_status !== "pending" && matchCrm(a));

  const Row = ({ a }) => {
    const st = a.attorney_status || "active";
    const sub = a.subscription_status || "none";
    return (
      <div className="bg-white rounded-2xl border border-slate-100 p-5 flex items-start justify-between gap-3 shadow-[0_8px_30px_rgb(0,0,0,0.04)]" data-testid={`attorney-${a.user_id}`}>
        <div className="flex items-start gap-3 min-w-0">
          <div className="h-10 w-10 rounded-xl bg-cobalt-light flex items-center justify-center font-heading font-bold text-cobalt shrink-0">{a.name?.[0]?.toUpperCase() || "A"}</div>
          <div className="min-w-0">
            <div className="font-semibold text-navy">{a.name} {a.firm_name && <span className="text-slate-400 font-normal">· {a.firm_name}</span>}</div>
            <div className="text-xs text-slate-500 truncate">{a.email}{a.phone ? ` · ${a.phone}` : ""}</div>
            {(a.bar_state || a.bar_number || a.office_location) && (
              <div className="text-xs text-slate-400 mt-0.5 flex items-center gap-1.5 flex-wrap">
                Bar {a.bar_state || "—"} #{a.bar_number || "—"} · {a.office_location || "—"}
                {a.bar_verified
                  ? <span className="inline-flex items-center gap-0.5 text-emerald-600 font-medium"><ShieldCheck className="w-3 h-3" /> verified</span>
                  : <span className="text-amber-500">unverified</span>}
              </div>
            )}
            {st !== "pending" && (
              <div className="text-xs text-slate-500 mt-1.5 flex items-center gap-2 flex-wrap" data-testid={`attorney-billing-${a.user_id}`}>
                <span className="font-medium text-navy">{a.membership_level || "No plan"}</span>
                <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${SUB[sub] || SUB.none}`}>{sub === "none" ? "unpaid" : sub}</span>
                <span>{money(a.monthly_price ?? 499)}/mo</span>
                <span className="text-slate-400">Claim {money(a.claim_fee ?? 100)} · Drone {money(a.drone_fee ?? 250)}</span>
                {a.last_payment_at && <span className="text-slate-400">· Paid {new Date(a.last_payment_at).toLocaleDateString()}</span>}
                {a.subscription_payment_failed && <span className="text-red-500 font-medium">· payment failed</span>}
              </div>
            )}
            {(a.crm_coverage || []).length > 0 && (
              <div className="mt-1.5 flex flex-wrap items-center gap-1.5" data-testid={`attorney-crm-${a.user_id}`}>
                <span className="text-[10px] font-bold text-cobalt uppercase tracking-wide">CRM</span>
                {a.crm_coverage.map((c) => (
                  <span key={c.provider} className="px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 text-[10px] font-semibold">✓ {c.name}{c.keep_synced ? " · Keep-in-Sync" : ""}</span>
                ))}
              </div>
            )}
            {a.demo && <span className="inline-block mt-1 px-2 py-0.5 rounded bg-amber-50 text-amber-600 text-[9px] font-bold uppercase tracking-wide" data-testid={`demo-badge-${a.user_id}`}>Demo Data</span>}
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0 flex-wrap justify-end">
          <span className={`text-xs font-semibold px-2.5 py-1 rounded-full capitalize ${STATUS[st]}`}>{st}</span>
          <button onClick={() => verifyBar(a.user_id)} data-testid={`attorney-verify-bar-${a.user_id}`} className={`flex items-center gap-1.5 px-3 py-2 rounded-full text-sm font-medium border ${a.bar_verified ? "border-emerald-200 text-emerald-600 bg-emerald-50" : "border-slate-200 text-slate-600 hover:bg-slate-50"}`}><ShieldCheck className="w-4 h-4" /> {a.bar_verified ? "Verified" : "Verify bar"}</button>
          {st === "pending" && <>
            <button onClick={() => setModalAttorney(a)} data-testid={`attorney-approve-${a.user_id}`} className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-cobalt text-white text-sm font-medium hover:bg-cobalt-hover"><CheckCircle2 className="w-4 h-4" /> Review & approve</button>
            <button onClick={() => setStatus(a.user_id, "rejected")} data-testid={`attorney-reject-${a.user_id}`} className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-white border border-slate-200 text-slate-600 text-sm hover:bg-red-50 hover:text-red-600"><XCircle className="w-4 h-4" /> Reject</button>
          </>}
          {st !== "pending" && (
            <button onClick={() => setBillingAttorney(a)} data-testid={`attorney-billing-btn-${a.user_id}`} className="flex items-center gap-1.5 px-3 py-2 rounded-full bg-navy text-white text-sm font-medium hover:opacity-90"><CreditCard className="w-4 h-4" /> Billing</button>
          )}
          {st === "active" && (
            <button onClick={() => setModalAttorney(a)} data-testid={`attorney-fees-${a.user_id}`} className="flex items-center gap-1.5 px-3 py-2 rounded-full bg-white border border-slate-200 text-navy text-sm font-medium hover:bg-slate-50"><DollarSign className="w-4 h-4 text-cobalt" /> Fees</button>
          )}
          {(st === "suspended" || st === "rejected") && <button onClick={() => setStatus(a.user_id, "active")} data-testid={`attorney-activate-${a.user_id}`} className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-cobalt text-white text-sm font-medium hover:bg-cobalt-hover"><Play className="w-4 h-4" /> Reinstate</button>}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-4" data-testid="admin-attorneys">
      {modalAttorney && <AdminAttorneyApproveModal attorney={modalAttorney} onClose={() => setModalAttorney(null)} onDone={load} />}
      {billingAttorney && <AdminBillingModal attorney={billingAttorney} onClose={() => setBillingAttorney(null)} onDone={load} />}
      <div className="flex items-center justify-between bg-white rounded-2xl border border-slate-100 p-4">
        <div><div className="text-sm font-medium text-navy">Auto-approve attorney applications</div><div className="text-xs text-slate-500">When off, new attorney signups require manual approval.</div></div>
        <button onClick={toggleAuto} data-testid="attorney-auto-approve" className={`w-11 h-6 rounded-full transition-colors relative ${autoApprove ? "bg-cobalt" : "bg-slate-300"}`}>
          <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-transform ${autoApprove ? "translate-x-5" : "translate-x-0.5"}`} />
        </button>
      </div>
      <div className="flex flex-wrap items-center gap-2 bg-white rounded-2xl border border-slate-100 p-3" data-testid="attorney-crm-filter">
        <span className="text-xs font-semibold text-slate-500 mr-1">Filter by CRM compatibility:</span>
        {[["", "All"], ["clio", "Supports Clio"], ["practicepanther", "Supports PracticePanther"], ["mycase", "Supports MyCase"], ["keep_sync", "Keep-in-Sync enabled"]].map(([k, l]) => (
          <button key={k} onClick={() => setCrmFilter(k)} data-testid={`crm-filter-${k || "all"}`}
            className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${crmFilter === k ? "bg-cobalt text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>{l}</button>
        ))}
      </div>
      {pending.length > 0 && (
        <div>
          <h3 className="font-heading font-semibold text-navy mb-2 flex items-center gap-2">Pending Approval <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">{pending.length}</span></h3>
          <div className="space-y-3">{pending.map((a) => <Row key={a.user_id} a={a} />)}</div>
        </div>
      )}
      <div>
        {pending.length > 0 && <h3 className="font-heading font-semibold text-navy mb-2 mt-4">All Attorneys</h3>}
        {others.length === 0 && pending.length === 0 ? <Card><p className="text-slate-500 text-center">No attorneys yet. Applications appear here for approval.</p></Card> :
          <div className="space-y-3">{others.map((a) => <Row key={a.user_id} a={a} />)}</div>}
      </div>
    </div>
  );
}

function UsersTab() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = () => { setLoading(true); api.get("/admin/users").then(({ data }) => setUsers(data)).finally(() => setLoading(false)); };
  useEffect(() => { load(); }, []);
  const modify = async (id, role, action) => {
    try { await api.post(`/admin/users/${id}/role`, { role, action }); toast.success(`${role} ${action === "grant" ? "granted" : "revoked"}`); load(); }
    catch { toast.error("Action failed"); }
  };
  if (loading) return <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 text-cobalt animate-spin" /></div>;
  return (
    <div className="space-y-3">
      {users.map((u) => {
        const roles = u.roles || [u.role];
        const isAttorney = roles.includes("attorney");
        const isAdmin = roles.includes("admin");
        return (
          <div key={u.user_id} className="bg-white rounded-2xl border border-slate-100 p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-3 shadow-[0_8px_30px_rgb(0,0,0,0.04)]" data-testid={`user-${u.user_id}`}>
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-xl bg-slate-100 flex items-center justify-center font-heading font-bold text-slate-500">{u.name?.[0]?.toUpperCase() || "U"}</div>
              <div>
                <div className="font-semibold text-navy">{u.name}</div>
                <div className="text-xs text-slate-500">{u.email}</div>
                <div className="flex gap-1 mt-1">
                  {roles.map((r) => (
                    <span key={r} className={`text-[10px] font-semibold px-2 py-0.5 rounded-full capitalize ${r === "admin" ? "bg-navy text-white" : r === "attorney" ? "bg-cobalt-light text-cobalt" : "bg-slate-100 text-slate-500"}`}>{r}</span>
                  ))}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              {isAttorney ? (
                <button onClick={() => modify(u.user_id, "attorney", "revoke")} className="px-3 py-2 rounded-full bg-white border border-slate-200 text-slate-600 text-xs font-medium hover:bg-slate-50 transition-all">Revoke Attorney</button>
              ) : (
                <button onClick={() => modify(u.user_id, "attorney", "grant")} data-testid={`grant-${u.user_id}`} className="flex items-center gap-1.5 px-3 py-2 rounded-full bg-cobalt-light text-cobalt text-xs font-semibold hover:bg-cobalt hover:text-white transition-all active:scale-95"><Scale className="w-3.5 h-3.5" /> Grant Attorney</button>
              )}
              {isAdmin ? (
                <button onClick={() => modify(u.user_id, "admin", "revoke")} className="px-3 py-2 rounded-full bg-white border border-slate-200 text-slate-600 text-xs font-medium hover:bg-slate-50 transition-all">Revoke Admin</button>
              ) : (
                <button onClick={() => modify(u.user_id, "admin", "grant")} data-testid={`grant-admin-${u.user_id}`} className="flex items-center gap-1.5 px-3 py-2 rounded-full bg-navy text-white text-xs font-semibold hover:opacity-90 transition-all active:scale-95"><ShieldCheck className="w-3.5 h-3.5" /> Grant Admin</button>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function TransfersTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => { api.get("/admin/transfers").then(({ data }) => setItems(data)).finally(() => setLoading(false)); }, []);
  if (loading) return <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 text-cobalt animate-spin" /></div>;
  if (items.length === 0) return <Card><p className="text-slate-500 text-center">No client transfers yet.</p></Card>;
  return (
    <div className="space-y-3">
      {items.map((t) => (
        <div key={t.case_id} className="bg-white rounded-2xl border border-slate-100 p-5 shadow-[0_8px_30px_rgb(0,0,0,0.04)]" data-testid={`transfer-${t.case_id}`}>
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-xl bg-cobalt-light flex items-center justify-center"><ArrowLeftRight className="w-5 h-5 text-cobalt" /></div>
              <div>
                <div className="font-semibold text-navy">{t.client_name} <span className="text-slate-400 font-normal">→</span> {t.attorney_name || "—"}</div>
                <div className="text-xs text-slate-500">{t.title} · {t.accepted_at ? new Date(t.accepted_at).toLocaleDateString() : "—"}</div>
              </div>
            </div>
            {t.invoice ? (
              <div className="text-right">
                <span className={`text-xs font-semibold px-3 py-1 rounded-full ${t.invoice.status === "paid" ? "bg-green-50 text-green-600" : "bg-amber-50 text-amber-600"}`}>{money(t.invoice.amount)} · {t.invoice.status}</span>
                <div className="text-[11px] text-slate-400 mt-1.5 flex items-center justify-end gap-1">
                  <Download className={`w-3 h-3 ${t.invoice.downloaded_at ? "text-green-500" : "text-slate-300"}`} />
                  {t.invoice.downloaded_at ? `Downloaded ${new Date(t.invoice.downloaded_at).toLocaleDateString()}` : "Not downloaded"}
                </div>
              </div>
            ) : <span className="text-xs text-slate-400">No invoice</span>}
          </div>
        </div>
      ))}
    </div>
  );
}

function InvoicesTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const token = getToken();
  const load = () => { setLoading(true); api.get("/admin/invoices").then(({ data }) => setItems(data)).finally(() => setLoading(false)); };
  useEffect(() => { load(); }, []);
  const setStatus = async (id, status) => {
    try { await api.post(`/admin/invoices/${id}/status`, { status }); toast.success(`Marked ${status}`); load(); }
    catch { toast.error("Action failed"); }
  };
  if (loading) return <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 text-cobalt animate-spin" /></div>;
  if (items.length === 0) return <Card><p className="text-slate-500 text-center">No invoices yet. They are created when an attorney accepts a case.</p></Card>;
  return (
    <div className="space-y-3">
      {items.map((i) => (
        <div key={i.invoice_id} className="bg-white rounded-2xl border border-slate-100 p-5 shadow-[0_8px_30px_rgb(0,0,0,0.04)]" data-testid={`invoice-${i.invoice_id}`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-xl bg-cobalt-light flex items-center justify-center"><ReceiptText className="w-5 h-5 text-cobalt" /></div>
              <div>
                <div className="font-semibold text-navy">{money(i.amount)} · {i.attorney_name || "Attorney"}</div>
                <div className="text-xs text-slate-500">{i.case_title} · invoiced {new Date(i.created_at).toLocaleDateString()}</div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${i.status === "paid" ? "bg-green-50 text-green-600" : "bg-amber-50 text-amber-600"}`}>{i.status}</span>
              <a href={`${process.env.REACT_APP_BACKEND_URL}/api/invoices/${i.invoice_id}/pdf?auth=${token}`} target="_blank" rel="noreferrer" data-testid={`invoice-pdf-${i.invoice_id}`} className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-white border border-slate-200 text-slate-700 text-sm font-medium hover:bg-slate-50 transition-all"><FileDown className="w-4 h-4" /> PDF</a>
              {i.status === "unpaid" ? (
                <button onClick={() => setStatus(i.invoice_id, "paid")} data-testid={`mark-paid-${i.invoice_id}`} className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-cobalt text-white text-sm font-medium hover:bg-cobalt-hover transition-all active:scale-95"><Check className="w-4 h-4" /> Mark Paid in Full</button>
              ) : (
                <button onClick={() => setStatus(i.invoice_id, "unpaid")} className="px-4 py-2 rounded-full bg-white border border-slate-200 text-slate-600 text-sm font-medium hover:bg-slate-50 transition-all">Undo</button>
              )}
            </div>
          </div>
          <div className="mt-3 pt-3 border-t border-slate-50 grid grid-cols-2 sm:grid-cols-3 gap-3 text-xs">
            <div className="flex items-center gap-1.5">
              <Download className={`w-3.5 h-3.5 ${i.downloaded_at ? "text-green-500" : "text-slate-300"}`} />
              <span className="text-slate-500">Downloaded:</span>
              <span className="font-medium text-navy">{i.downloaded_at ? new Date(i.downloaded_at).toLocaleString() : "Not yet"}</span>
            </div>
            <div className="flex items-center gap-1.5">
              <ReceiptText className="w-3.5 h-3.5 text-slate-400" />
              <span className="text-slate-500">Downloads:</span>
              <span className="font-medium text-navy">{i.download_count || 0}</span>
            </div>
            <div className="flex items-center gap-1.5">
              <Check className={`w-3.5 h-3.5 ${i.paid_at ? "text-green-500" : "text-slate-300"}`} />
              <span className="text-slate-500">Paid:</span>
              <span className="font-medium text-navy">{i.paid_at ? new Date(i.paid_at).toLocaleString() : "Unpaid"}</span>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function ScoringTab() {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  const load = () => api.get("/admin/scoring-config").then(({ data }) => setCfg(data));
  useEffect(() => { load(); }, []);

  const setOptPoints = (cat, key, val) => {
    setCfg((c) => {
      const copy = structuredClone(c);
      const o = copy.categories[cat].options.find((x) => x.key === key);
      o.points = Number(val);
      return copy;
    });
  };
  const setOptGrade = (cat, key, val) => {
    setCfg((c) => {
      const copy = structuredClone(c);
      const o = copy.categories[cat].options.find((x) => x.key === key);
      o.grade = val;
      return copy;
    });
  };
  const setScale = (i, field, val) => {
    setCfg((c) => {
      const copy = structuredClone(c);
      copy.grade_scale[i][field] = Number(val);
      return copy;
    });
  };

  const save = async () => {
    setSaving(true);
    try { const { data } = await api.put("/admin/scoring-config", cfg); setCfg(data); toast.success("Scoring config saved"); }
    catch { toast.error("Save failed"); } finally { setSaving(false); }
  };
  const reset = async () => {
    try { const { data } = await api.post("/admin/scoring-config/reset"); setCfg(data); toast.success("Reset to defaults"); }
    catch { toast.error("Reset failed"); }
  };

  if (!cfg) return <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 text-cobalt animate-spin" /></div>;

  const numCls = "w-20 bg-slate-50 border border-slate-200 rounded-lg px-2 py-1.5 text-sm outline-none focus:border-cobalt text-right";

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-500">Adjust point values and grade thresholds. Changes apply to newly calculated scores.</p>
        <div className="flex gap-2">
          <button onClick={reset} className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-white border border-slate-200 text-slate-600 text-sm font-medium hover:bg-slate-50 transition-all"><RotateCcw className="w-4 h-4" /> Reset</button>
          <button onClick={save} disabled={saving} data-testid="save-scoring-btn" className="px-5 py-2 rounded-full bg-cobalt text-white text-sm font-medium hover:bg-cobalt-hover transition-all">{saving ? "Saving…" : "Save Config"}</button>
        </div>
      </div>

      <Card>
        <h3 className="font-heading font-semibold text-navy">Grade Thresholds</h3>
        <div className="mt-3 space-y-2">
          {cfg.grade_scale.map((g, i) => (
            <div key={g.grade} className="flex items-center gap-3 text-sm">
              <span className="w-8 font-heading font-bold" style={{ color: (cfg.grade_colors || {})[g.grade] }}>{g.grade}</span>
              <input type="number" className={numCls} value={g.min} onChange={(e) => setScale(i, "min", e.target.value)} />
              <span className="text-slate-400">to</span>
              <input type="number" className={numCls} value={g.max} onChange={(e) => setScale(i, "max", e.target.value)} />
            </div>
          ))}
        </div>
      </Card>

      <Card>
        <h3 className="font-heading font-semibold text-navy">Injury Point Values</h3>
        <div className="mt-3 grid sm:grid-cols-2 gap-2">
          {cfg.categories.injury.options.map((o) => (
            <div key={o.key} className="flex items-center justify-between gap-3 text-sm">
              <span className="text-slate-600">{o.label}</span>
              <input type="number" className={numCls} value={o.points} onChange={(e) => setOptPoints("injury", o.key, e.target.value)} data-testid={`score-injury-${o.key}`} />
            </div>
          ))}
        </div>
      </Card>

      {["coverage", "liability"].map((cat) => (
        <Card key={cat}>
          <h3 className="font-heading font-semibold text-navy">{cfg.categories[cat].label}</h3>
          <div className="mt-3 space-y-2">
            {cfg.categories[cat].options.map((o) => (
              <div key={o.key} className="flex items-center justify-between gap-3 text-sm">
                <span className="text-slate-600 flex-1">{o.label}</span>
                <input value={o.grade || ""} onChange={(e) => setOptGrade(cat, o.key, e.target.value)} className="w-16 bg-slate-50 border border-slate-200 rounded-lg px-2 py-1.5 text-sm outline-none focus:border-cobalt text-center" placeholder="Grade" />
                <input type="number" className={numCls} value={o.points} onChange={(e) => setOptPoints(cat, o.key, e.target.value)} />
              </div>
            ))}
          </div>
        </Card>
      ))}
    </div>
  );
}

function ResponseNetworkTab() {
  const [pilots, setPilots] = useState([]);
  const [dispatches, setDispatches] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [pendingPayouts, setPendingPayouts] = useState([]);
  const [payouts, setPayouts] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = () => {
    setLoading(true);
    Promise.all([
      api.get("/admin/pilots"), api.get("/admin/dispatches"),
      api.get("/admin/deployment-analytics").catch(() => ({ data: null })),
      api.get("/admin/pilot-payouts/pending").catch(() => ({ data: [] })),
      api.get("/admin/pilot-payouts").catch(() => ({ data: [] })),
    ]).then(([p, d, a, pp, po]) => {
      setPilots(p.data); setDispatches(d.data); setAnalytics(a.data);
      setPendingPayouts(pp.data); setPayouts(po.data);
    }).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);
  const setStatus = async (id, status) => {
    try { await api.put(`/admin/pilots/${id}/status`, { status }); toast.success(`Pilot ${status}`); load(); }
    catch { toast.error("Action failed"); }
  };
  const runPayout = async (pilot_id) => {
    try { await api.post("/admin/pilot-payouts/run", { pilot_id, provider: "stripe_connect" }); toast.success("Payout initiated (sandbox)"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };
  const markPaid = async (payout_id) => {
    try { await api.post(`/admin/pilot-payouts/${payout_id}/mark-paid`); toast.success("Marked paid"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };
  if (loading) return <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 text-cobalt animate-spin" /></div>;
  const badge = { pending: "bg-amber-50 text-amber-600", approved: "bg-green-50 text-green-600", suspended: "bg-slate-100 text-slate-500" };
  return (
    <div className="space-y-6" data-testid="admin-response-network">
      {analytics && (
        <div data-testid="admin-deployment-analytics">
          <h3 className="font-heading font-semibold text-navy mb-3">Deployment Analytics</h3>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[["Total dispatches", analytics.total_dispatches], ["Completed", analytics.completed],
              ["Acceptance rate", `${analytics.acceptance_rate}%`], ["Completion rate", `${analytics.completion_rate}%`]].map(([l, v]) => (
              <div key={l} className="bg-white rounded-2xl border border-slate-100 p-4 shadow-sm">
                <div className="text-xs text-slate-500">{l}</div>
                <div className="text-2xl font-heading font-extrabold text-navy mt-1">{v}</div>
              </div>
            ))}
          </div>
          {analytics.coverage_by_state?.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-2">
              {analytics.coverage_by_state.slice(0, 8).map((c) => (
                <span key={c.state} className="text-xs bg-slate-100 text-slate-600 px-2.5 py-1 rounded-full">{c.state}: {c.count}</span>
              ))}
            </div>
          )}
        </div>
      )}

      <div data-testid="admin-payouts">
        <h3 className="font-heading font-semibold text-navy mb-3">Pilot Payouts <span className="text-xs font-normal text-amber-500 bg-amber-50 px-2 py-0.5 rounded-full">Sandbox</span></h3>
        <div className="space-y-2">
          {pendingPayouts.length === 0 ? <Card><p className="text-slate-500 text-center text-sm">No pending pilot earnings.</p></Card> :
            pendingPayouts.map((p) => (
              <div key={p.pilot_id} className="bg-white rounded-2xl border border-slate-100 p-4 flex items-center justify-between gap-3" data-testid={`payout-pending-${p.pilot_id}`}>
                <div><div className="font-medium text-navy text-sm">{p.name}</div><div className="text-xs text-slate-500">{p.deployments} deployment(s) · ${p.pending.toFixed(2)} pending</div></div>
                <button onClick={() => runPayout(p.pilot_id)} data-testid={`run-payout-${p.pilot_id}`} className="px-4 py-2 rounded-full bg-cobalt text-white text-sm font-medium hover:bg-cobalt-hover">Pay ${p.pending.toFixed(2)}</button>
              </div>
            ))}
          {payouts.filter((x) => x.status === "processing").map((x) => (
            <div key={x.payout_id} className="bg-white rounded-2xl border border-slate-100 p-4 flex items-center justify-between gap-3" data-testid={`payout-proc-${x.payout_id}`}>
              <div><div className="font-medium text-navy text-sm">{x.pilot_name} · ${x.amount?.toFixed(2)}</div><div className="text-xs text-slate-500">{x.provider_name} · {x.reference} · processing</div></div>
              <button onClick={() => markPaid(x.payout_id)} data-testid={`mark-paid-${x.payout_id}`} className="px-4 py-2 rounded-full bg-emerald-500 text-white text-sm font-medium hover:bg-emerald-600">Mark paid</button>
            </div>
          ))}
        </div>
      </div>

      <AdminPilotApplications />
      <div>
        <h3 className="font-heading font-semibold text-navy mb-3">Dispatches ({dispatches.length})</h3>
        <div className="space-y-2">
          {dispatches.length === 0 ? <Card><p className="text-slate-500 text-center">No dispatches yet.</p></Card> :
            dispatches.slice(0, 40).map((d) => (
              <div key={d.dispatch_id} className="bg-white rounded-2xl border border-slate-100 p-4 flex items-center justify-between gap-3">
                <div>
                  <div className="font-medium text-navy text-sm">{d.case_number} <span className="text-xs text-slate-400">· {d.location_city ? `${d.location_city}, ` : ""}{d.location_state || "—"}</span></div>
                  <div className="text-xs text-slate-500">{d.assigned_pilot_name ? `Pilot: ${d.assigned_pilot_name}` : "Unassigned"}{d.fee ? ` · $${d.fee}` : ""}{d.payment ? ` · ${d.payment.status}` : ""}</div>
                </div>
                <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-slate-100 text-slate-600 capitalize">{d.status.replace("_", " ")}</span>
              </div>
            ))}
        </div>
      </div>
    </div>
  );
}

function NotifyTab() {
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const send = async () => {
    if (!title.trim()) { toast.error("Add a title"); return; }
    setSending(true);
    try { await api.post("/admin/notify", { title, body }); toast.success("Sent to all attorneys"); setTitle(""); setBody(""); }
    catch { toast.error("Could not send"); }
    finally { setSending(false); }
  };
  return (
    <Card>
      <h3 className="font-heading font-semibold text-navy flex items-center gap-2"><Bell className="w-5 h-5 text-cobalt" /> Broadcast to Attorneys</h3>
      <p className="text-sm text-slate-500 mt-1">Send an in-app notification to every active attorney.</p>
      <div className="mt-4 space-y-3 max-w-xl">
        <input value={title} onChange={(e) => setTitle(e.target.value)} data-testid="notify-title" placeholder="Title" className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 outline-none focus:border-cobalt" />
        <textarea value={body} onChange={(e) => setBody(e.target.value)} data-testid="notify-body" placeholder="Message" className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 outline-none focus:border-cobalt min-h-[100px]" />
        <button onClick={send} disabled={sending} data-testid="notify-send" className="flex items-center gap-2 px-6 py-3 rounded-full bg-cobalt text-white font-medium hover:bg-cobalt-hover transition-all active:scale-95"><Send className="w-4 h-4" /> {sending ? "Sending…" : "Send Notification"}</button>
      </div>
    </Card>
  );
}
